<script setup lang="ts">
/**
 * A group of sidebar rows under an uppercase label („ANSICHTEN“, „MOUNTS“).
 * `meta` puts a quiet note right after the label (a count: „2/3 sichtbar“);
 * `actions` holds small buttons at the end of the label row. Plain buttons
 * there get the section's quiet icon look; kit buttons (NxIconButton) keep
 * their own, including the `pressed` state of a toggle.
 */
defineProps<{
  label?: string;
  /** Quiet note after the label, e.g. a count. */
  meta?: string;
}>();
</script>

<template>
  <nav class="cn-nav-section">
    <div v-if="label || meta || $slots.actions" class="cn-nav-header">
      <div v-if="label" class="cn-nav-label">{{ label }}</div>
      <span v-if="meta" class="cn-nav-meta">{{ meta }}</span>
      <div v-if="$slots.actions" class="cn-nav-actions">
        <slot name="actions" />
      </div>
    </div>
    <slot />
  </nav>
</template>

<style scoped>
.cn-nav-section {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}
/* A section that fills the sidebar (a long folder tree) must not squeeze its label row. */
.cn-nav-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  margin-top: var(--nxt-space-2xs);
  min-height: 1.125em;
}
.cn-nav-label {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
/* Right after the label; the auto margin keeps the actions at the end. */
.cn-nav-meta {
  min-width: 0;
  margin-inline: var(--nxt-space-xs) auto;
  overflow: hidden;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cn-nav-actions {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  color: var(--nxt-fg-subtle);
}
/* Plain buttons only: kit buttons carry a `variant-*` class and style themselves. */
.cn-nav-actions :deep(button:not([class*='variant-'])) {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  color: var(--nxt-fg-subtle);
  display: grid;
  place-items: center;
}
.cn-nav-actions :deep(button:not([class*='variant-']):hover) {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-fg);
}
</style>
